import {
    ADVANCED_SCHEDULING,
    PDF_IMPORT_V1,
    PLAN_PAGE_LOADBOARD,
    PLAN_PAGE_NETWORK_ROUTES,
    PLAN_PAGE_PAST_SCHEDULED,
    BROKER,
    CUSTOM_CUSTOMER_COMMS_RANGE,
    WMS,
    CDS_TIMEFRAME_EXPORT,
    DELIVERY_SLA,
    ONWARD_HANDLED_QUOTES,
    PLAN_PAGE_REMOVE_WITHOUT_UNLOCK,
    ADD_ORDER_TO_COMPLETED_ROUTE,
    SHIPPER_RATE_SHOP,
    PLAN_PAGE_SHOW_RATES,
    ASSET_HEAVY,
    ROUTE_CUSTOM_MAP_MARKERS,
    SALESFORCE_ROUTE_EXPORT,
    GENERAL_ELECTRIC_JSON_IMPORT,
    INGEST_MANIFESTS,
    PRESCHEDULED_HIGHLIGHT,
    SHOW_REC_EQUIPMENT_SHAPE_TAGS,
    AUTO_SPLIT_ITEM_QUANTITY,
    AUTO_ALLOCATE_FORWARD_STOCKED,
    SKIP_COMINGLED_STOPS,
    GE_DELIVERS_ROUTE_EXPORT,
    ROUTE_RETURNS_MANIFEST,
    APPROXIMATE_FACILITY_LOCATION,
    MASTERBRAND_CUSTOM_EXPORT,
    PELOTON_CUSTOM_EXPORT,
    CATALOG_SERVICE_RATES,
    SPLIT_ORDERS,
} from '@/constants/featureFlags';

export const featureFlagGroups = [
    {
        label: 'Feature Unlocks',
        flags: [
            {
                name: 'saas-v1',
                description: 'Enables access to Software-as-a-Service components (Planning Page, etc.)',
            },
            { name: SHIPPER_RATE_SHOP, description: 'Enables rate shop for shippers' },
            {
                name: ASSET_HEAVY,
                description:
                    'Carrier locations owned by the Shipper/Org. This gives carriers access to shipper/admin level invoicing for T1 orders',
            },
            {
                name: WMS,
                description: 'Enables full access to WMS features for internal orders',
            },
            {
                name: BROKER,
                description: `This flag marks the account as a broker and enables broker feature for the account.`,
            },
            {
                name: ADVANCED_SCHEDULING,
                description: `Advanced scheduling for allocating regions / scheduling resources for shippers / carriers`,
            },
            {
                name: DELIVERY_SLA,
                description:
                    'Enabled access to delivery SLA related fields such as estimated delivery start/end ranges',
            },
            {
                name: SPLIT_ORDERS,
                description:
                    'Allows orders for this shipper to be split into child orders. Orders that are already split stay split if this is turned off.',
            },
            {
                name: 'customerNewShipmentForm',
                description: `This flag enables the shipper to list their locations on the customer new shipment form.`,
            },
        ],
    },
    {
        label: 'Planning & Dispatch',
        flags: [
            {
                name: PLAN_PAGE_LOADBOARD,
                description: 'Enables Load Board display on the plan page',
            },
            {
                name: PLAN_PAGE_NETWORK_ROUTES,
                description: 'Enables Network Routes toggle on the plan page',
            },
            {
                name: 'plan-page-recommendations',
                description: 'Enables searching for order recommendations on the plan page',
            },
            {
                name: 'plan-page-default-map',
                description: 'Reverts the Google map in Planning back to the standard Google color pallet',
            },
            {
                name: 'plan-page-order-pickups',
                description: 'Shows the pickup flag on the Planning map',
            },
            {
                name: 'plan-page-marker-clustering',
                description: 'Clusteres nearby markers on the plan page',
            },
            {
                name: 'plan-page-mapbox',
                description:
                    'Experimental MapBox features on the plan page. This includes route optimization around locked timeframes.',
            },
            {
                name: 'plan-page-unscheduled',
                description: 'Show unscheduled orders in Planning.',
            },
            {
                name: 'plan-page-received',
                description: 'Show received, unscheduled orders in Planning.',
            },
            {
                name: PLAN_PAGE_PAST_SCHEDULED,
                description:
                    'Show past scheduled orders in Planning. Brings in "missed" orders (scheduled and still unrouted, up to 30 days before the plan date) plus unscheduled orders.',
            },
            {
                name: PLAN_PAGE_REMOVE_WITHOUT_UNLOCK,
                description:
                    'Allows user to remove stops from routes in planning without having to unlock the route first.',
            },
            {
                name: PLAN_PAGE_SHOW_RATES,
                description:
                    'Enables carriers to see financial data (shipper rates, carrier revenue, driver pay) on routes in the plan page.',
            },
            {
                name: 'plan-reschedule-v1',
                description: 'Enables access to the re-scheduling feature.',
            },
            {
                name: 'custom-timeframes-beta',
                description: 'Allow users to select custom timeframes in planning.',
            },
            {
                name: 'lock-all-routes',
                description: `This flag will lock all routes immediately but does not impact sms requests and responses.`,
            },
            {
                name: 'overnight-routes',
                description: `This flag allows overnight stops/routes. It displays a toggle in dispatch planning where a dispatcher may mark a warehouse pickup stop as 'overnight', in the case of a driver loading the truck up the day before delivery. For routes with an overnight pickup, a start location is required (would be the driver's hotel they are staying at overnight), and route optimization uses this start location, whilst ignoring the overnight warehouse pickup stop from optimization.`,
            },
            {
                name: ADD_ORDER_TO_COMPLETED_ROUTE,
                description: `Carrier can add orders to completed routes for the day, in case of wanting to send the driver on an additional order.`,
            },
            {
                name: PRESCHEDULED_HIGHLIGHT,
                description: 'Highlights prescheduled orders with a gold color on the plan page and map tooltips.',
            },
            {
                name: ROUTE_CUSTOM_MAP_MARKERS,
                description:
                    'Enables clients to see their custom shapes in route view in DispatchPlan and RouteDetails',
            },
            {
                name: CDS_TIMEFRAME_EXPORT,
                description: `Allow users to export a CSV of timeframes on the plan page in a format that may be uploaded into CDS.`,
            },
            {
                name: SALESFORCE_ROUTE_EXPORT,
                description: 'Enables salesforce csv export format on plan page',
            },
            {
                name: GE_DELIVERS_ROUTE_EXPORT,
                description: 'Enables GE Delivers csv export format on plan page',
            },
            {
                name: SKIP_COMINGLED_STOPS,
                description: `If enabled for a shipper, delivery stops will automatically be marked complete and skipped on mobile. Users will be expected to complete information for the respective stops via a 3rd party app. (Used by GE at the moment)`,
            },
        ],
    },
    {
        label: 'Communications',
        flags: [
            {
                name: 'disable-customer-availability',
                description: 'Disables customer availability sms/email upon order confirmation',
            },
            {
                name: 'disable-review-sms-messages',
                description: 'Disables customer rating/review sms messages on order completion',
            },
            {
                name: 'sms-alert-next-stop-customers',
                description: 'An sms alert for customers when they are the next stop on a route',
            },
            {
                name: 'customer-emails-with-sms',
                description:
                    'Whenever SMS communications are sent to customers, an email will be sent to the customer as well with the corresponding information.',
            },
            {
                name: 'disable-tracking-comms',
                description: `Suppresses customer tracking links (SMS and email). This applies if EITHER party to a delivery has it enabled — the order's shipper or the dispatching carrier — so enabling it on one account also affects orders it shares with the other. Shippers can also set this themselves on their account profile.`,
            },
            {
                name: 'hide-tracking-link-phone',
                description: `By default tracking links include a phone number to the store for customers to contact in case of any issues. Enabling this flag removes the phone number portion of the tracking link.`,
            },
            {
                name: APPROXIMATE_FACILITY_LOCATION,
                description: `On the tracking link, show the final-mile facility as a shaded circle covering its general area instead of a pin on its exact address, so customers can't drive there to collect the order themselves. The customer's own address still shows as an exact pin, and live truck tracking is unaffected once the order leaves the facility.`,
            },
            {
                name: 'delay-predelivery-comms',
                description: `Don't receive predelivery sms until after the order has been received by the carrier for marketplace orders`,
            },
            {
                name: 'predelivery-delay-mm',
                description: `If enabled, middle mile orders will send a predelivery sms to customers notifying them of the order and confirming their address info.`,
            },
            {
                name: 'notify-predelivery-comments',
                description: `If enabled, the order dispatcher will receive a notification when a customer adds special instructions to an order.`,
            },
            {
                name: '40-min-tracking-comms',
                description: `For accounts with this flag enabled, tracking links will replace the text saying "15-30 minutes" with "approximately 40 minutes".`,
            },
            {
                name: 'sales-representative-comms',
                description: `When comms are sent on behalf of an account with this flag, their phone number is replaced with "Please contact your sales representative"`,
            },
            {
                name: 'forward-rejected-timeframe-confirmations',
                description: `When a customer presses 2 ("this date does not work") on the timeframe confirmation robocall, forward the call to the account's support number so they can speak with a representative directly, instead of only reading the number aloud. Requires a valid support/business phone; the sales-representative-comms flag takes precedence.`,
            },
            {
                name: 'send-livingspaces-postdel-text',
                description: `Customers for this shipper will get the following post delivery text instead of our default one: "Hi {customer name}, it was a pleasure delivering your Living Spaces order. You will receive a quick email survey from them within 24 to 48 hour. Your feedback means a lot and gets you $25 off your next $100+ purchase."`,
            },
            {
                name: 'disable-admin-emails',
                description: `All emails to an account are also sent to all admin users for that account. This flag disables that functionality so that only the main account email will receive emails sent to an account.`,
            },
            {
                name: 'disable-received-comms',
                description: 'Disable emails and in app notifications when an order has been marked as received',
            },
            {
                name: 'force-sms-comms',
                description: `If toggled on for a shipper, consignees will receive all of the normal SMS communications from Onward, regardless of Carrrier TMS. Normal behavior when this is toggled off is we shut off our communications and let the external TMS handle them if applicable (DispatchTrack, Xcelerator, etc.) Turning this on may result in duplicated messaging from us and external tms to the consignee.`,
            },
            {
                name: CUSTOM_CUSTOMER_COMMS_RANGE,
                description: `Allows user to specify a time range on their account profile page in which certain customer communications will only be sent within this range. Default otherwise is 8am to 8pm.`,
            },
            {
                name: 'bidding-comms-disabled',
                description: `This flag disables additional quoting communications. This includes emails to the shipper when a carrier has claimed a load or placed a quote, and emails to either party when the other side has submitted or awarded a quote or counter-offer. For quoting comms that apply to both shippers and carriers, this feature flag must be enabled for each receiving party.`,
            },
        ],
    },
    {
        label: 'Accounting & Billing',
        flags: [
            {
                name: 'accounting-v2',
                description: 'Enables access to version 2 of the accounting features (CompletedOrders, Invoices)',
            },
            {
                name: 'accounting-generate-invoices',
                description:
                    'Automatically generate invoices for orders placed by this shipper. Invoices will generate on Thursday and include orders completed the previous week (Monday - Sunday).',
            },
            {
                name: 'accounting-credit-limit-api',
                description:
                    'Disable API access to placing new orders when this shipper is over the credit limit. Orders that are invoiced but not paid contribute towards the credit limit.',
            },
            {
                name: 'accounting-credit-limit-ui',
                description:
                    'Disable UI access to placing new orders when this shipper is over the credit limit. Orders that are invoiced but not paid contribute towards the credit limit.',
            },
            {
                name: 'driver-payments',
                description: 'Enables access to Driver Payments and triggers auto generation of them',
            },
            {
                name: 'hide-market-rates',
                description: `Should be turned on for accounts with contract rates. Hides Onward Market rates for the user across the platform and instead says 'Contract Rates'. If an admin has overridden the order's rates via the admin load board, these rates will display instead of the 'Contract Rates' text for the user.`,
            },
            {
                name: 'electrolux-custom-export',
                description: 'Enables the Electrolux custom accounting export format.',
            },
            {
                name: MASTERBRAND_CUSTOM_EXPORT,
                description:
                    "Enables the MasterBrand custom CSV export format. It appears on the Receivables page (when every selected invoice belongs to a client with it enabled) and on the invoice detail page, and is attached to the client's NetSuite invoice on approval. Receivable invoices only.",
            },
            {
                name: PELOTON_CUSTOM_EXPORT,
                description:
                    'Enables the Peloton "Invoice Detailed" CSV export: one row per invoice line item. It appears on the Receivables page (when every selected invoice belongs to a client with it enabled, aggregated into one file) and on the invoice detail page, and is attached to the client\'s NetSuite invoice on approval. Receivable invoices only.',
            },
            {
                name: CATALOG_SERVICE_RATES,
                description:
                    'Lets this shipper set a rate on each service in their Services catalog, saved as a service rate row on each of their tariffs. Pricing charges it on the shipper rate when a service line has no other rate row; carrier pay for the line stays $0. New scoped services are no longer forced to Non Billable. Turning this off hides the field and stops charging these rates; the rows stay on the tariffs. Currently implemented for GE algo only.',
            },
        ],
    },
    {
        label: 'Marketplace & Quoting',
        flags: [
            {
                name: 'ltl-marketplace',
                description: 'Enables access to submitting LTL orders to the marketplace',
            },
            {
                name: 'ftl-marketplace',
                description: 'Enables access to submitting FTL routes to the marketplace',
            },
            {
                name: 'bidding-disabled',
                description: `This flag disables auction features for the account. Shippers can still create marketplace orders but carriers will only be able to claim`,
            },
            {
                name: ONWARD_HANDLED_QUOTES,
                description: `Obfuscates most of the quoting process from this shipper, allowing Onward admins to handle carrier offers and quoting manually (mainly for HOW clients). Shipper can still submit auctionable orders, but don't have access to the normal auction information. Instead, Onward admins will handle quoting and select a final price and send a quote to the shipper. Normal auction comms also turned off if this is enabled.`,
            },
            {
                name: 'shipper-bidding-v1',
                description: `This flag enables shippers to view and place quotes on other shippers' listings.`,
            },
        ],
    },
    {
        label: 'Delivery & Driver',
        flags: [
            {
                name: 'require-digital-signature',
                description: `Drivers will be required to obtain a digital signature for an order before being able to mark delivery as complete`,
            },
            {
                name: 'require-signature-name',
                description: `Drivers will be required to obtain the customer's typed out name for an order before being able to mark delivery as complete`,
            },
            {
                name: 'require-loading-scan',
                description: `Drivers will be required to scan item/pallet labels when loading an order on to a truck`,
            },
            {
                name: 'require-delivery-scan',
                description: `Drivers will be required to scan item/pallet labels when delivering an order.`,
            },
            {
                name: 'autoconfirm-all-stops',
                description: `This flag will autocomplete all stops and bypass the need to manually confirm stop timeframes.`,
            },
            {
                name: 'curbside-no-autoconfirm',
                description: `By default curbside deliveries are auto confirmed. Be enabling this flag, curbside deliveries will no longer be auto confirmed.`,
            },
            {
                name: 'curbside-no-timeframe',
                description: `Enabling this flag will remove the 2/4 time window on the planning confirmation text for curbside stops when submitting a route`,
            },
            {
                name: 'business-type-autoconfirm',
                description: `Automatically confirm timeframes for business type deliveries.`,
            },
            {
                name: 'hide-driver-phone-number',
                description: `Hide driver's phone number from the customer tracking links.`,
            },
            {
                name: 'mobile-item-tracking',
                description: `Users with this enabled will see a new "tracking" tab when they log in to the mobile app. This tab is used to scan certain item events during the delivery flow from receiving to delivery. These tracking events are normally baked in to our tms flow in other parts of the mobile app, so this special tracking tab is only necessary for carriers that are not using our tms, and their tms does not provide all the proper item events via api (like DispatchTrack)`,
            },
            {
                name: 'box-piece-count',
                description: `Enables box/piece count requirement for orders.`,
            },
            {
                name: 'carrier-milestone-thresholds',
                description: `Enables milestone thresholds for carriers for internal orders.`,
            },
        ],
    },
    {
        label: 'Order Import',
        flags: [
            {
                name: PDF_IMPORT_V1,
                description: `allow user to import pdf orders`,
            },
            {
                name: GENERAL_ELECTRIC_JSON_IMPORT,
                description: `Allow GE users to import Salesforce JSON order files`,
            },
            {
                name: INGEST_MANIFESTS,
                description:
                    'When an ASN number is assigned to an order, auto-creates the inbound manifest for that ASN and attaches the order. Scoped to the warehouse carrier — enable on the carrier receiving the freight.',
            },
            {
                name: ROUTE_RETURNS_MANIFEST,
                description:
                    "A route's return orders auto-populate a route-linked inbound manifest. Scoped to the route's carrier — enable on the carrier delivering the route.",
            },
            {
                name: AUTO_SPLIT_ITEM_QUANTITY,
                description:
                    'When importing orders, automatically expand any item with a quantity greater than 1 into individual line items each with a quantity of 1.',
            },
            {
                name: AUTO_ALLOCATE_FORWARD_STOCKED,
                description:
                    'Forward-stocked orders auto-allocate matching on-hand stock at ingest, oldest first, without the shipper naming units. Items with no matching stock are left for the warehouse to pick by hand. Scoped to the warehouse carrier — enable on the carrier holding the stock.',
            },
            {
                name: 'import-default-mm-fm',
                description: `When importing orders, set default toggles to create orders needing a MM + FM delivery`,
            },
            {
                name: 'import-default-receiving',
                description: `When importing orders, set default toggles to create receiving orders. These are orders with a middle mile leg that will be performed by the shipper outside of the Onward system.`,
            },
            {
                name: 'import-lock-wg-light-assembly',
                description: `When importing orders, lock the item assembly level to 'light assembly' for white glove orders, and disabled for other service levels.`,
            },
            {
                name: 'import-disable-curbside-threshold',
                description: `When importing orders, disable the curbside threshold (first dry space) service levels`,
            },
        ],
    },
    {
        label: 'Misc.',
        flags: [
            {
                name: 'auto-assign-by-zip',
                description: `Auto assign API orders to carriers by the consignee zip code. If a carrier is not found for that zip code the order will be rejected.`,
            },
            {
                name: 'auto-assign-by-zip-passthrough',
                description: `For auto assign via cosignee zip, if a carrier is not found for that zip code send the order to the marketplace.`,
            },
            {
                name: 'auto-assign-carrier',
                description: `Some shippers will have contracts with a certain carrier, and will use them exclusively. You can set that carrier here, and all orders created by the shipper will be automatically assigned to them.`,
            },
            {
                name: 'auto-assign-mp-comms',
                description: `This re-enables some marketplace related communications that are typically disabled for orders automatically assign to a carrier. This includes an internal slack message and an email to the shipper indicating that the order has been submitted to the marketplace.`,
            },
            {
                name: 'api-disable-query',
                description: 'Disables the query APIs for this client',
            },
            {
                name: 'api-filter-query-locked-routes',
                description: 'Filters route query APIs to only locked routes',
            },
            {
                name: 'bypass-rec-equipment',
                description:
                    "Skips automatic recommended-equipment generation entirely for this client's orders — no 2-man team, dollies, lift gate, etc. are auto-added. Use for clients whose equipment is handled manually or via service levels so orders rate without auto-added equipment. Does not affect equipment set manually or by an integration.",
            },
            {
                name: SHOW_REC_EQUIPMENT_SHAPE_TAGS,
                description:
                    'Includes auto-recommended equipment in the order-tag map shapes. When off (the default), only route tags and manual equipment overrides appear in the shape selector.',
            },
        ],
    },
];

// Export previous format of feature-flags for imports throughout the app.
export const featureFlags = featureFlagGroups.flatMap((group) => group.flags);
